import { useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import FailedIcon from 'src/assets/icons/FailedIcon';
import Button from 'src/components/design-system/Button';
import useBoolean from 'src/hooks/useBoolean';
import useCompany from 'src/hooks/useCompany';
import useInvoiceScanManager from 'src/hooks/useInvoiceScanManager';
import useRunOcrScanApi from 'src/services/useRunOcrScanApi';
import DeleteConfirmationModal from './DeleteConfirmationModal';

const headerWebhookError =
  'Unable to scan this file. Please click on Rerun OCR to try again. If it continues to fail, then click Abandon Scan and upload a new file to try again (Error: Error while processing the header webhook)';
const itemWebhookError =
  'Unable to process this file. Please click on Rerun OCR to try again. If it continues to fail, then click Abandon Scan and upload a new file to try again (Error: Error while processing the item webhook)';
const invalidFileError =
  'Unable to read this scan. Please click on Abandon Scan to upload a new file and try again or manually create the document (Error: Invalid file content type)';
const uploadingError =
  'Unable to upload this file. Please delete and re-upload to try again (Error: File not exists in input blob container for DIP processing)';

// Error type constants
const ERROR_TYPES = {
  HEADER_WEBHOOK: 'Error while processing the header webhook',
  ITEM_WEBHOOK: 'Error while processing the Item webhook',
  MAX_TOKEN_LIMIT: 'Exception occured in OpenAI- Max token limit reached',
  INVALID_FILE_CONTENT: 'Invalid file content type',
  FILE_NOT_EXISTS: 'File not exists in input blob container for DIP processing',
} as const;

// Error configuration mapping
interface ErrorConfig {
  message: string;
  canRerunOcr: boolean;
  canDeleteAndMove: boolean;
}

const ERROR_CONFIGS: Record<string, ErrorConfig> = {
  [ERROR_TYPES.HEADER_WEBHOOK]: {
    message: headerWebhookError,
    canRerunOcr: true,
    canDeleteAndMove: true,
  },
  [ERROR_TYPES.ITEM_WEBHOOK]: {
    message: itemWebhookError,
    canRerunOcr: true,
    canDeleteAndMove: true,
  },
  [ERROR_TYPES.MAX_TOKEN_LIMIT]: {
    message: ERROR_TYPES.MAX_TOKEN_LIMIT,
    canRerunOcr: true,
    canDeleteAndMove: true,
  },
  [ERROR_TYPES.INVALID_FILE_CONTENT]: {
    message: invalidFileError,
    canRerunOcr: false,
    canDeleteAndMove: false,
  },
  [ERROR_TYPES.FILE_NOT_EXISTS]: {
    message: uploadingError,
    canRerunOcr: false,
    canDeleteAndMove: false,
  },
};
const FailedStatusPreview = () => {
  const { previewDocument, refreshPage, ocrFailedValue, enableRefresh, disableRefresh } =
    useInvoiceScanManager();
  const { company: selectedCompany } = useCompany();
  const { ocrScanRun, isOcrScanRunning } = useRunOcrScanApi();
  const { value: isModalOpen, setTrue: openModal, setFalse: closeModal } = useBoolean();
  const attachmentId = useMemo(
    () => (previewDocument?.attachId ? [previewDocument.attachId] : []),
    [previewDocument?.attachId]
  );

  const { errorMessage, canDeleteAndMove, canRerunOcr } = useMemo(() => {
    const config = ERROR_CONFIGS[ocrFailedValue || ''];
    if (config) {
      return {
        errorMessage: config.message,
        canRerunOcr: config.canRerunOcr,
        canDeleteAndMove: config.canDeleteAndMove,
      };
    }

    return {
      errorMessage: ocrFailedValue,
      canRerunOcr: false,
      canDeleteAndMove: false,
    };
  }, [ocrFailedValue]);

  const handleReRunOcr = useCallback(async () => {
    disableRefresh();
    try {
      await ocrScanRun({
        companyId: Number(selectedCompany.companyId),
        attachments: attachmentId.map((id) => ({ attachmentId: id })),
      });
      toast.success('Rerun OCR Scan has been successfully completed.');
      refreshPage();
    } catch (error) {
      toast.error('Failed to rerun OCR scan. Please try again.');
      console.error('OCR rerun error:', error);
    } finally {
      enableRefresh();
    }
  }, [
    attachmentId,
    disableRefresh,
    enableRefresh,
    ocrScanRun,
    refreshPage,
    selectedCompany.companyId,
  ]);

  const handleAbandonScan = useCallback(() => {
    disableRefresh();
    openModal();
  }, [openModal, disableRefresh]);

  return (
    <div className="flex flex-col gap-1 items-center mb-1">
      <div className="flex flex-row gap-[6px] p-[6px] rounded-3xl bg-[#D9534F33] items-center justify-center">
        <FailedIcon />
        <span className="text-[12px] font-medium">Error with scan</span>
      </div>
      <div className="text-center text-[14px] px-2">{errorMessage}</div>
      <div className="flex flex-row justify-center flex-wrap">
        <DeleteConfirmationModal
          attachmentIds={attachmentId}
          canDeleteAndMove={canDeleteAndMove}
          modalState="failed"
          onClose={closeModal}
          visible={isModalOpen}
        />
        <Button color="error" onClick={handleAbandonScan} disabled={isOcrScanRunning}>
          Abandon Scan
        </Button>
        {canRerunOcr && (
          <Button color="info" onClick={handleReRunOcr} loading={isOcrScanRunning}>
            Rerun OCR
          </Button>
        )}
      </div>
    </div>
  );
};

export default FailedStatusPreview;
